import BaseDrawer from "@stardust/base-drawer";
import AddToCart from "@stardust/add-to-cart";
import { escapeHtml } from "@stardust/utilities";

// Event name constants (previously from events.js)
const Events = {
  quickViewDrawer: { open: 'quick-view-drawer:open', close: 'quick-view-drawer:close' },
  keyboard: { esc: 'keydown:escape' },
};

/**
 * Product Quick View Modal - Bottom sheet style modal for viewing product details
 * Opens to 90% viewport height from the bottom
 */
class ProductQuickViewDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      // We use data-state attribute for transforms, so these are empty
      openClass: '',
      closeClass: '',
      animationDuration: 300,
      overlayZIndex: 9999,
    });

    // DOM element references
    this.loadingEl = element.querySelector('[data-quick-view-loading]');
    this.productEl = element.querySelector('[data-quick-view-product]');
    this.errorEl = element.querySelector('[data-quick-view-error]');
    this.titleEl = element.querySelector('[data-quick-view-title]');
    this.productTitleEl = element.querySelector('[data-quick-view-product-title]');
    this.productPriceEl = element.querySelector('[data-quick-view-product-price]');
    this.mainImageEl = element.querySelector('[data-quick-view-main-image]');
    this.thumbnailsEl = element.querySelector('[data-quick-view-thumbnails]');
    this.optionsEl = element.querySelector('[data-quick-view-options]');
    this.quantityEl = element.querySelector('[data-quick-view-quantity]');
    this.addToCartBtn = element.querySelector('[data-quick-view-add-to-cart]');
    this.fullLinkEl = element.querySelector('[data-quick-view-full-link]');
    this.descriptionEl = element.querySelector('[data-quick-view-description]');

    // Current product data
    this.currentProduct = null;
    this.selectedVariant = null;
    this.addToCartInstance = null;
  }

  load() {


    // Listen for open event
    document.addEventListener(Events.quickViewDrawer.open, (e) => {
      const { productHandle, product } = e.detail || {};
      if (product) {
        // Product data passed directly
        this.showProduct(product);
        this.open();
      } else if (productHandle) {
        // Need to fetch product data
        this.open();
        this.fetchAndShowProduct(productHandle);
      }
    });

    // Listen for close event
    document.addEventListener(Events.quickViewDrawer.close, this._boundClose);

    // Listen for ESC key
    document.addEventListener(Events.keyboard.esc, () => {
      if (this.isOpen) {
        this.close();
      }
    });

    // Quantity controls
    const qtyMinus = this.element.querySelector('[data-quick-view-qty-minus]');
    const qtyPlus = this.element.querySelector('[data-quick-view-qty-plus]');

    if (qtyMinus) {
      qtyMinus.addEventListener('click', () => {
        const current = parseInt(this.quantityEl.value, 10) || 1;
        if (current > 1) {
          this.quantityEl.value = current - 1;
        }
      });
    }

    if (qtyPlus) {
      qtyPlus.addEventListener('click', () => {
        const current = parseInt(this.quantityEl.value, 10) || 1;
        this.quantityEl.value = current + 1;
      });
    }

    // Retry button
    const retryBtn = this.element.querySelector('[data-quick-view-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => {
        if (this.currentProductHandle) {
          this.fetchAndShowProduct(this.currentProductHandle);
        }
      });
    }


  }

  /**
   * Fetch product data by handle and display
   */
  async fetchAndShowProduct(handle) {
    this.currentProductHandle = handle;
    this.showLoading();

    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product');

      const product = await response.json();
      this.showProduct(product);
    } catch (error) {
      console.error('[QuickView] Failed to fetch product:', error);
      this.showError();
    }
  }

  /**
   * Display product in the modal
   */
  showProduct(product) {
    this.currentProduct = product;
    this.selectedVariant = product.variants?.[0] || null;

    // Update modal title
    if (this.titleEl) {
      this.titleEl.textContent = 'Quick View';
    }

    // Update product title
    if (this.productTitleEl) {
      this.productTitleEl.textContent = product.title || '';
    }

    // Update price
    this.updatePrice();

    // Update main image
    if (this.mainImageEl && product.featured_image) {
      this.mainImageEl.src = product.featured_image;
      this.mainImageEl.alt = product.title || '';
    }

    // Build thumbnails
    this.buildThumbnails(product);

    // Build options
    this.buildOptions(product);

    // Update full link
    if (this.fullLinkEl) {
      this.fullLinkEl.href = product.url || `/products/${product.handle}`;
    }

    // Update description
    if (this.descriptionEl) {
      // Description might have HTML, sanitize or render it
      this.descriptionEl.innerHTML = product.description || '';
    }

    // Reset quantity
    if (this.quantityEl) {
      this.quantityEl.value = 1;
    }

    // Initialize AddToCart utility
    if (this.productEl) {
      this.addToCartInstance = new AddToCart({
        container: this.productEl,
        product: product,
        onSuccess: () => {
          // Close quick view after successful add to cart
          setTimeout(() => this.close(), 500);
        },
        onError: (error) => {
          console.error('[QuickView] Add to cart failed:', error);
        }
      });
    }

    // Show product content, hide loading
    this.hideLoading();
    this.hideError();
    if (this.productEl) {
      this.productEl.classList.remove('hidden');
    }
  }

  /**
   * Update price display
   */
  updatePrice() {
    if (!this.productPriceEl) return;

    const variant = this.selectedVariant;
    if (!variant) return;

    const formatMoney = window.theme?.formatMoney || ((cents) => `$${(cents / 100).toFixed(2)}`);

    let priceHtml = formatMoney(variant.price);

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHtml = `
        <span class="text-red-600">${formatMoney(variant.price)}</span>
        <span class="text-gray-400 line-through text-sm ml-2">${formatMoney(variant.compare_at_price)}</span>
      `;
    }

    this.productPriceEl.innerHTML = priceHtml;
  }

  /**
   * Build thumbnail gallery
   */
  buildThumbnails(product) {
    if (!this.thumbnailsEl || !product.images?.length) return;

    const html = product.images.slice(0, 6).map((image, index) => `
      <button 
        type="button"
        class="w-16 h-16 rounded-lg overflow-hidden border-2 ${index === 0 ? 'border-[#16ADE4]' : 'border-transparent'} hover:border-[#16ADE4] transition-colors flex-shrink-0"
        data-quick-view-thumb="${index}"
      >
        <img 
          src="${typeof image === 'string' ? image : image.src}" 
          alt=""
          class="w-full h-full object-cover"
        >
      </button>
    `).join('');

    this.thumbnailsEl.innerHTML = html;

    // Add click handlers
    this.thumbnailsEl.querySelectorAll('[data-quick-view-thumb]').forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.quickViewThumb, 10);
        const image = product.images[index];

        if (this.mainImageEl && image) {
          this.mainImageEl.src = typeof image === 'string' ? image : image.src;
        }

        // Update active state
        this.thumbnailsEl.querySelectorAll('[data-quick-view-thumb]').forEach(t => {
          t.classList.remove('border-[#16ADE4]');
          t.classList.add('border-transparent');
        });
        thumb.classList.remove('border-transparent');
        thumb.classList.add('border-[#16ADE4]');
      });
    });
  }

  /**
   * Build variant option selectors
   */
  buildOptions(product) {
    if (!this.optionsEl || !product.options?.length || !product.variants?.length) {
      if (this.optionsEl) this.optionsEl.innerHTML = '';
      return;
    }

    // Skip if only one variant with default title
    if (product.variants.length === 1 && product.variants[0].title === 'Default Title') {
      this.optionsEl.innerHTML = '';
      return;
    }

    const html = product.options.map((option, index) => {
      const optionName = typeof option === 'string' ? option : (option.name || option);
      const optionKey = `option${index + 1}`;
      const uniqueValues = [...new Set(product.variants.map(v => v[optionKey]).filter(Boolean))];

      if (uniqueValues.length <= 1) return '';

      const optionsHtml = uniqueValues.map(val =>
        `<option value="${escapeHtml(val)}">${escapeHtml(val)}</option>`
      ).join('');

      return `
        <div class="mb-3">
          <label class="block text-sm font-medium mb-1">${escapeHtml(optionName)}</label>
          <select 
            class="w-full border rounded-lg px-3 py-2 bg-white"
            data-quick-view-option="${index}"
          >
            ${optionsHtml}
          </select>
        </div>
      `;
    }).filter(Boolean).join('');

    this.optionsEl.innerHTML = html;

    // Add change handlers
    this.optionsEl.querySelectorAll('[data-quick-view-option]').forEach(select => {
      select.addEventListener('change', () => {
        this.updateSelectedVariant();
      });
    });
  }

  /**
   * Update selected variant based on option selections
   */
  updateSelectedVariant() {
    if (!this.currentProduct?.variants) return;

    const selects = this.optionsEl.querySelectorAll('[data-quick-view-option]');
    const selectedOptions = Array.from(selects).map(s => s.value);

    // Find matching variant
    const variant = this.currentProduct.variants.find(v => {
      return selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt);
    });

    if (variant) {
      this.selectedVariant = variant;
      this.updatePrice();

      // Update main image if variant has featured image
      if (variant.featured_image && this.mainImageEl) {
        this.mainImageEl.src = variant.featured_image.src || variant.featured_image;
      }
    }
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.classList.remove('hidden');
    if (this.productEl) this.productEl.classList.add('hidden');
    if (this.errorEl) this.errorEl.classList.add('hidden');
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.classList.add('hidden');
  }

  showError() {
    if (this.loadingEl) this.loadingEl.classList.add('hidden');
    if (this.productEl) this.productEl.classList.add('hidden');
    if (this.errorEl) this.errorEl.classList.remove('hidden');
  }

  hideError() {
    if (this.errorEl) this.errorEl.classList.add('hidden');
  }

  /**
   * Override onOpen to set data-state attribute for CSS animation
   */
  onOpen() {
    this.element.dataset.state = 'open';
  }

  /**
   * Override onClose to reset state
   */
  onClose() {
    this.element.dataset.state = 'closed';
    // Reset to loading state for next open
    this.showLoading();
    if (this.productEl) this.productEl.classList.add('hidden');
    this.currentProduct = null;
    this.selectedVariant = null;
  }
}

// Initialize
const init = () => {
  const productQuickViewDrawer = document.querySelector('[data-product-quick-view-drawer]');
  if (productQuickViewDrawer) new ProductQuickViewDrawer(productQuickViewDrawer).load();
};
init();

export default ProductQuickViewDrawer;
